import { ok, err, type Result } from 'neverthrow';
import type { DecoderFunction } from '../decoder.js';
import { z } from 'zod';
import { Comparable } from '../comparable.js';

enum IdType {
  AccountGroupId,
  ExternalAccountEntryId,
  FaultGroup,
}

export type AnySimpleId = SimpleId<IdType>;

abstract class SimpleId<T> extends Comparable<SimpleId<T>> {
  // Required to differentiate classes since TS uses structural
  // rather than nominal typing
  protected typeMarker?: T;
  constructor(private _id: string) {
    super();
  }

  toJSON(): string {
    return this._id;
  }

  // Do not change method name -- it overrides the default toString method
  // and is relied on to generate the ID in string format.
  public override toString(): string {
    return this._id;
  }

  public stringId(): string {
    return this._id;
  }

  public equals(other: SimpleId<T>) {
    return this._id === other._id;
  }

  public greaterThan(other: SimpleId<T>) {
    return this._id > other._id;
  }

  public lessThan(other: SimpleId<T>) {
    return this._id < other._id;
  }
}

export class FaultGroupId extends SimpleId<IdType.FaultGroup> {}
export class AccountGroupId extends SimpleId<IdType.AccountGroupId> {}
export class ExternalAccountEntryId extends SimpleId<IdType.ExternalAccountEntryId> {}

//
// Branded types implementations
//

/** A regex for any UUID-like identifier: 8-4-4-4-12 hex pattern */
const guid: RegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;

function isUuid(value: string): boolean {
  return guid.test(value);
}
function isUuidWithCustomByte(customByte: 'ac'): (value: string) => value is AccountId;
function isUuidWithCustomByte(customByte: 'e1'): (value: string) => value is EmployerId;
function isUuidWithCustomByte(customByte: 'ca'): (value: string) => value is CaseId;
function isUuidWithCustomByte(customByte: 'c1'): (value: string) => value is ClientId;
function isUuidWithCustomByte(customByte: 'f1'): (value: string) => value is FileId;
function isUuidWithCustomByte(customByte: '75'): (value: string) => value is RecurringSearchId;
function isUuidWithCustomByte(customByte: '11'): (value: string) => value is QueryId;
function isUuidWithCustomByte(customByte: '01'): (value: string) => value is OutcomeId;
function isUuidWithCustomByte(customByte: '51'): (value: string) => value is SampleId;
function isUuidWithCustomByte(customByte: 'fa'): (value: string) => value is FaultId;
function isUuidWithCustomByte(customByte: '60'): (value: string) => value is PortalRequestId;
function isUuidWithCustomByte(customByte: '61'): (value: string) => value is PortalThreadId;
function isUuidWithCustomByte(customByte: '62'): (value: string) => value is PortalMessageId;
function isUuidWithCustomByte(customByte: '63'): (value: string) => value is PortalFileId;
function isUuidWithCustomByte(customByte: string): (value: string) => boolean {
  return (value) => isUuid(value) && (value.endsWith(customByte) || value.startsWith(customByte));
}

function makeUuidBrandedId<T extends string>(
  isBrand: (v: string) => v is T,
  name: string,
): (value: string) => Result<T, string> {
  return (value: string) => {
    if (isBrand(value)) {
      return ok(value);
    }
    return err(`String "${value}" is not a valid UUID ${name} id`);
  };
}

/**
 * Turns a fallible smart constructor into a co-located `{ schema, decoder }`
 * pair for a branded string: the Zod schema validates and brands on decode, the
 * `decoder` is the throwing `DecoderFunction`. Pair with {@link makeUuidBrandedId}
 * for UUID ids. Private on purpose — every branded id is defined in this module,
 * so the factory is never a public export.
 */
function makeBrandedStringCoders<T extends string>(
  make: (value: string) => Result<T, string>,
): {
  schema: z.ZodType<T, string>;
  decoder: DecoderFunction<T>;
} {
  return {
    schema: z.string().transform((value, ctx) => {
      const result = make(value);
      if (result.isErr()) {
        ctx.addIssue({ code: 'custom', message: result.error });
        return z.NEVER;
      }
      return result.value;
    }),
    decoder: (value: unknown) => {
      const result = make(z.string().parse(value));
      if (result.isErr()) {
        throw result.error;
      }
      return result.value;
    },
  };
}

declare const brand: unique symbol;
type Branded<T, BrandName extends string> = T & { readonly [brand]: BrandName };

export type EmployerId = Branded<string, 'EmployerId'>;
const makeEmployerId = makeUuidBrandedId(isUuidWithCustomByte('e1'), 'employer');
export const { schema: employerIdSchema, decoder: employerIdDecoder } =
  makeBrandedStringCoders(makeEmployerId);

export type AccountId = Branded<string, 'AccountId'>;
const makeAccountId = makeUuidBrandedId(isUuidWithCustomByte('ac'), 'account');
export const { schema: accountIdSchema, decoder: accountIdDecoder } =
  makeBrandedStringCoders(makeAccountId);

export type CaseId = Branded<string, 'CaseId'>;
const makeCaseId = makeUuidBrandedId(isUuidWithCustomByte('ca'), 'case');
export const { schema: caseIdSchema, decoder: caseIdDecoder } = makeBrandedStringCoders(makeCaseId);

export type ClientId = Branded<string, 'ClientId'>;
const makeClientId = makeUuidBrandedId(isUuidWithCustomByte('c1'), 'client');
export const { schema: clientIdSchema, decoder: clientIdDecoder } =
  makeBrandedStringCoders(makeClientId);

export type FileId = Branded<string, 'FileId'>;
const makeFileId = makeUuidBrandedId(isUuidWithCustomByte('f1'), 'file');
export const { schema: fileIdSchema, decoder: fileIdDecoder } = makeBrandedStringCoders(makeFileId);

export type RecurringSearchId = Branded<string, 'RecurringSearchId'>;
const makeRecurringSearchId = makeUuidBrandedId(isUuidWithCustomByte('75'), 'recurring search');
export const { schema: recurringSearchIdSchema, decoder: recurringSearchIdDecoder } =
  makeBrandedStringCoders(makeRecurringSearchId);

export type QueryId = Branded<string, 'QueryId'>;
const makeQueryId = makeUuidBrandedId(isUuidWithCustomByte('11'), 'query');
export const { schema: queryIdSchema, decoder: queryIdDecoder } =
  makeBrandedStringCoders(makeQueryId);

export type OutcomeId = Branded<string, 'OutcomeId'>;
const makeOutcomeId = makeUuidBrandedId(isUuidWithCustomByte('01'), 'outcome');
export const { schema: outcomeIdSchema, decoder: outcomeIdDecoder } =
  makeBrandedStringCoders(makeOutcomeId);

export type SampleId = Branded<string, 'SampleId'>;
const makeSampleId = makeUuidBrandedId(isUuidWithCustomByte('51'), 'sample');
export const { schema: sampleIdSchema, decoder: sampleIdDecoder } =
  makeBrandedStringCoders(makeSampleId);

//
// Plain UUID ids (no custom byte), mirroring the Rust newtypes in
// df_id/src/types.rs that derive UuidType without `#[custom_byte]`.
//

export type DocumentId = Branded<string, 'DocumentId'>;
const makeDocumentId = makeUuidBrandedId((value): value is DocumentId => isUuid(value), 'document');
export const { schema: documentIdSchema, decoder: documentIdDecoder } =
  makeBrandedStringCoders(makeDocumentId);

export type GroupSigningProcessId = Branded<string, 'GroupSigningProcessId'>;
const makeGroupSigningProcessId = makeUuidBrandedId(
  (value): value is GroupSigningProcessId => isUuid(value),
  'group signing process',
);
export const { schema: groupSigningProcessIdSchema, decoder: groupSigningProcessIdDecoder } =
  makeBrandedStringCoders(makeGroupSigningProcessId);

export type UserTaskId = Branded<string, 'UserTaskId'>;
const makeUserTaskId = makeUuidBrandedId(
  (value): value is UserTaskId => isUuid(value),
  'user task',
);
export const { schema: userTaskIdSchema, decoder: userTaskIdDecoder } =
  makeBrandedStringCoders(makeUserTaskId);

export type EmailId = Branded<string, 'EmailId'>;
const makeEmailId = makeUuidBrandedId((value): value is EmailId => isUuid(value), 'email');
export const { schema: emailIdSchema, decoder: emailIdDecoder } =
  makeBrandedStringCoders(makeEmailId);

export type MessageId = Branded<string, 'MessageId'>;
const makeMessageId = makeUuidBrandedId((value): value is MessageId => isUuid(value), 'message');
export const { schema: messageIdSchema, decoder: messageIdDecoder } =
  makeBrandedStringCoders(makeMessageId);

export type RecordId = Branded<string, 'RecordId'>;
const makeRecordId = makeUuidBrandedId((value): value is RecordId => isUuid(value), 'record');
export const { schema: recordIdSchema, decoder: recordIdDecoder } =
  makeBrandedStringCoders(makeRecordId);

export type ResponseId = Branded<string, 'ResponseId'>;
const makeResponseId = makeUuidBrandedId((value): value is ResponseId => isUuid(value), 'response');
export const { schema: responseIdSchema, decoder: responseIdDecoder } =
  makeBrandedStringCoders(makeResponseId);

export type ThreadId = Branded<string, 'ThreadId'>;
const makeThreadId = makeUuidBrandedId((value): value is ThreadId => isUuid(value), 'thread');
export const { schema: threadIdSchema, decoder: threadIdDecoder } =
  makeBrandedStringCoders(makeThreadId);

export type PersonId = Branded<string, 'PersonId'>;
const makePersonId = makeUuidBrandedId((value): value is PersonId => isUuid(value), 'person');
export const { schema: personIdSchema, decoder: personIdDecoder } =
  makeBrandedStringCoders(makePersonId);

export type AceId = Branded<string, 'AceId'>;
const makeAceId = makeUuidBrandedId((value): value is AceId => isUuid(value), 'ace');
export const { schema: aceIdSchema, decoder: aceIdDecoder } = makeBrandedStringCoders(makeAceId);

// df_id UUID newtypes (FaultId carries `#[custom_byte = "fa"]`; AccountingNoteId is a plain UUID).
export type FaultId = Branded<string, 'FaultId'>;
const makeFaultId = makeUuidBrandedId(isUuidWithCustomByte('fa'), 'fault');
export const { schema: faultIdSchema, decoder: faultIdDecoder } =
  makeBrandedStringCoders(makeFaultId);

// The customer portal's Förfrågan. These are named by the API, so they are uuids rather than
// integers: a customer-facing integer id would enumerate across tenants.
export type PortalRequestId = Branded<string, 'PortalRequestId'>;
const makePortalRequestId = makeUuidBrandedId(isUuidWithCustomByte('60'), 'portal request');
export const { schema: portalRequestIdSchema, decoder: portalRequestIdDecoder } =
  makeBrandedStringCoders(makePortalRequestId);

export type PortalThreadId = Branded<string, 'PortalThreadId'>;
const makePortalThreadId = makeUuidBrandedId(isUuidWithCustomByte('61'), 'portal thread');
export const { schema: portalThreadIdSchema, decoder: portalThreadIdDecoder } =
  makeBrandedStringCoders(makePortalThreadId);

export type PortalMessageId = Branded<string, 'PortalMessageId'>;
const makePortalMessageId = makeUuidBrandedId(isUuidWithCustomByte('62'), 'portal message');
export const { schema: portalMessageIdSchema, decoder: portalMessageIdDecoder } =
  makeBrandedStringCoders(makePortalMessageId);

export type PortalFileId = Branded<string, 'PortalFileId'>;
const makePortalFileId = makeUuidBrandedId(isUuidWithCustomByte('63'), 'portal file');
export const { schema: portalFileIdSchema, decoder: portalFileIdDecoder } =
  makeBrandedStringCoders(makePortalFileId);

export type AccountingNoteId = Branded<string, 'AccountingNoteId'>;
const makeAccountingNoteId = makeUuidBrandedId(
  (value): value is AccountingNoteId => isUuid(value),
  'accounting note',
);
export const { schema: accountingNoteIdSchema, decoder: accountingNoteIdDecoder } =
  makeBrandedStringCoders(makeAccountingNoteId);

// EntryId has no `df_id` UUID newtype — it is an arbitrary string identifier, so brand without a format check.
export type EntryId = Branded<string, 'EntryId'>;
const makeEntryId = (value: string): Result<EntryId, string> => ok(value as EntryId);
export const { schema: entryIdSchema, decoder: entryIdDecoder } =
  makeBrandedStringCoders(makeEntryId);

//
// File ids of integration-owned files, mirroring df_id/src/integration_ids.rs.
//

/** `f<uuid>` for a file, `s<number>` for a supplier invoice, `i<number>` for an invoice. */
const fortnoxFileIdPattern: RegExp = /^[fsi].+$/;

export type FortnoxFileId = Branded<string, 'FortnoxFileId'>;
const makeFortnoxFileId = (value: string): Result<FortnoxFileId, string> =>
  fortnoxFileIdPattern.test(value)
    ? ok(value as FortnoxFileId)
    : err(`String "${value}" is not a valid Fortnox file id`);
export const { schema: fortnoxFileIdSchema, decoder: fortnoxFileIdDecoder } =
  makeBrandedStringCoders(makeFortnoxFileId);

export type VismaFileId = Branded<string, 'VismaFileId'>;
const makeVismaFileId = makeUuidBrandedId(
  (value): value is VismaFileId => isUuid(value),
  'Visma file',
);
export const { schema: vismaFileIdSchema, decoder: vismaFileIdDecoder } =
  makeBrandedStringCoders(makeVismaFileId);

/** The backend names the id field `fileId` on every source, so a value read off one is any of the three. */
export type AnyFileId = FileId | FortnoxFileId | VismaFileId;
// Most specific first, so a Visma UUID that happens to start with `f` is not read as Fortnox.
const makeAnyFileId = (value: string): Result<AnyFileId, string> =>
  makeFileId(value)
    .orElse(() => makeVismaFileId(value))
    .orElse(() => makeFortnoxFileId(value))
    .mapErr(() => `String "${value}" is not a valid file id`);
export const { schema: anyFileIdSchema, decoder: anyFileIdDecoder } =
  makeBrandedStringCoders(makeAnyFileId);
